import htmlToPlainText from './htmlToPlainText'
import { extractNoteImageUrls, isSameImageUrl } from './noteImages'

/**
 * Client-side search over a notes feed.
 *
 * Every notes surface already loads its whole set into Redux (or local state,
 * for the portfolio roll-up), so filtering happens in the browser. That keeps
 * results instant and needs no text index; if notes ever grow past what is
 * reasonable to hold client-side, this is the seam to move server-side.
 */

/** One image row, as far as search is concerned. */
export interface SearchableImage {
	url: string
	note?: string
}

/**
 * Everything about a note a query can match: its text, plus the descriptions on
 * its images.
 *
 * The note body is flattened to plain text first. Searching the raw HTML would
 * match tag names and Cloudinary URLs, so a query like "img" or "upload" would
 * hit every note carrying a picture.
 */
export const buildNoteSearchText = (
	noteHtml: string | null | undefined,
	imageRecords?: SearchableImage[],
): string => {
	// maxLength 0 disables truncation — the default cap is for display, and
	// silently dropping the tail of a long note would make it unsearchable.
	const parts: string[] = [htmlToPlainText(noteHtml, 0)]

	if (imageRecords?.length) {
		const urls = extractNoteImageUrls(noteHtml)
		for (const url of urls) {
			const record = imageRecords.find(candidate => isSameImageUrl(candidate.url, url))
			if (record?.note) parts.push(record.note)
		}
	}

	return parts.filter(Boolean).join(' ').toLowerCase()
}

/**
 * True when every whitespace-separated term appears somewhere in `haystack`.
 *
 * All terms must match, so "roof damage" finds a note reading "damage to the
 * roof" — narrowing as the user types, which is what a filter box should do.
 * An empty or whitespace-only query matches everything.
 */
export const matchesSearch = (haystack: string, query: string): boolean => {
	const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
	if (terms.length === 0) return true
	return terms.every(term => haystack.includes(term))
}

/** Filters a list of notes by the query, matching text and image descriptions. */
export const filterNotesBySearch = <T extends { note?: string }>(
	notes: T[],
	query: string,
	imageRecords?: SearchableImage[],
): T[] => {
	if (!query.trim()) return notes
	return notes.filter(note => matchesSearch(buildNoteSearchText(note.note, imageRecords), query))
}
